/**
 * PulseItemsFeed — public list of community-submitted Neighborhood Pulse posts.
 * Renders on both /pulse (global feed) and /pulse/{slug} (locality feed).
 *
 * Props:
 *   slug      – optional. If set, filters to a specific neighborhood.
 *   country   – optional country filter.
 *   canAdd    – bool. Shows the "Add a post" toggle button (requires auth).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2, Pencil, MapPin, Loader2, ExternalLink, Youtube, Instagram, Globe, Radio, Calendar, Star } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import PulseItemForm from "@/components/PulseItemForm";
import Linkify from "@/components/Linkify";
import { paletteFor } from "@/lib/palette";

const CARD_TILE = {
  event: { color: "#B25D38", Icon: Calendar, label: "EVENT" },
  news:  { color: "#0891B2", Icon: Radio,    label: "NEWS" },
  info:  { color: "#059669", Icon: Radio,    label: "INFO" },
};
function tileFor(item) {
  if (item?.category && CARD_TILE[item.category]) return CARD_TILE[item.category];
  const tags = (item?.tags || []).map((t) => (t || "").toLowerCase());
  if (tags.includes("events")) return CARD_TILE.event;
  if (tags.includes("news")) return CARD_TILE.news;
  return { color: "var(--wofy-primary)", Icon: Radio, label: "PULSE" };
}

export default function PulseItemsFeed({ slug, country, city, state, lat, lng, canAdd = true }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (slug) params.set("slug", slug);
      if (country) params.set("country", country);
      if (city) params.set("city", city);
      params.set("limit", "24");
      const { data } = await http.get(`/pulse-items?${params.toString()}`);
      setItems(data.items || []);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [slug, country, city]);

  useEffect(() => { load(); }, [load]);

  const onSaved = (item) => {
    setItems((prev) => {
      const filtered = prev.filter((p) => p.id !== item.id);
      return [item, ...filtered];
    });
    setShowForm(false); setEditing(null);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await http.delete(`/pulse-items/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-10" data-testid="pulse-items-feed">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl">Community posts</h2>
        {canAdd && !showForm && (
          user ? (
            <button onClick={() => { setEditing(null); setShowForm(true); }} className="wofy-btn-primary text-sm" data-testid="pulse-add-btn">
              <Plus className="w-4 h-4" /> Add a post
            </button>
          ) : (
            <Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`} className="wofy-btn-primary text-sm" data-testid="pulse-signin-cta">
              <Plus className="w-4 h-4" /> Sign in to post
            </Link>
          )
        )}
      </div>

      {showForm && (
        <div className="mb-6">
          <PulseItemForm
            neighborhoodSlug={slug}
            city={city}
            state={state}
            country={country}
            lat={lat}
            lng={lng}
            editing={editing}
            onSaved={onSaved}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        </div>
      )}

      {loading && (
        <div className="text-sm inline-flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading posts…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="wofy-card text-center py-10" style={{ color: "var(--wofy-text-soft)" }}>
          No community posts yet. {canAdd && user ? "Be the first — click Add a post above." : "Sign in to add one."}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((p) => (
          <PulseItemCard
            key={p.id}
            item={p}
            isMine={user && p.user_id === user.id}
            onEdit={() => { setEditing(p); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            onDelete={() => del(p.id)}
          />
        ))}
      </div>
    </div>
  );
}

function PulseItemCard({ item, isMine, onEdit, onDelete }) {
  const tile = tileFor(item);
  const TileIcon = tile.Icon;
  const detailHref = `/pulse-item/${item.id}`;
  // Colourful palette derived from the item id so each card in the grid
  // is visually distinct from its neighbours (matches family-tree cards).
  const palette = paletteFor(item.id || item.title);
  return (
    <article
      className="rounded-2xl overflow-hidden flex flex-col group border transition-all hover:-translate-y-0.5 hover:shadow-lg"
      style={{ borderColor: `${palette.accent}33`, background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)` }}
      data-testid={`pulse-item-${item.id}`}
    >
      <Link to={detailHref} className="block" data-testid={`pulse-item-open-${item.id}`}>
        {item.image_url ? (
          <div className="aspect-video overflow-hidden bg-black">
            <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
          </div>
        ) : (
          <div
            className="aspect-video flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${palette.accent}22, ${palette.accent}55)` }}
            data-testid={`pulse-item-fallback-${item.id}`}
          >
            <div className="text-center">
              <TileIcon className="w-10 h-10 mx-auto" style={{ color: palette.accent }} />
              <div className="mt-1 font-mono uppercase tracking-widest text-[10px]" style={{ color: palette.accent }}>{tile.label}</div>
            </div>
          </div>
        )}
      </Link>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-2 text-xs mb-2" style={{ color: palette.ink, opacity: 0.75 }}>
          <Avatar src={item.user_avatar} name={item.user_name} size={24} />
          {item.user_handle ? (
            <Link to={`/u/${item.user_handle}`} className="hover:underline">{item.user_name || "Member"}</Link>
          ) : (
            <span>{item.user_name || "Member"}</span>
          )}
          <span className="opacity-50">·</span>
          <time className="font-mono opacity-70">{formatTime(item.created_at)}</time>
        </div>

        <Link to={detailHref} className="block hover:opacity-80" data-testid={`pulse-item-title-${item.id}`}>
          <h3 className="font-display text-lg leading-tight flex items-start gap-1.5">
            {item.is_featured && (
              <span
                title="Featured on homepage"
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0 mt-1"
                style={{ background: "var(--wofy-primary)", color: "#fff" }}
                data-testid={`pulse-item-featured-${item.id}`}
              >
                <Star className="w-2.5 h-2.5" style={{ fill: "#fff" }} /> Featured
              </span>
            )}
            <span>{item.title}</span>
          </h3>
        </Link>
        {item.description && (
          <p className="mt-2 text-sm line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>
            <Linkify text={item.description} />
          </p>
        )}

        {item.tags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {item.tags.slice(0, 6).map((t) => (
              <span key={t} className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>#{t}</span>
            ))}
          </div>
        )}

        <div className="mt-auto pt-3 border-t flex items-center justify-between gap-3 text-xs" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
            {(item.city || item.country) && (
              <span className="inline-flex items-center gap-1 truncate">
                <MapPin className="w-3 h-3" /> {[item.city, item.country].filter(Boolean).join(", ")}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link to={detailHref} className="hover:underline" style={{ color: "var(--wofy-primary)" }} data-testid={`pulse-item-view-${item.id}`}>
              View →
            </Link>
            {item.website_url && <a href={item.website_url} target="_blank" rel="noopener noreferrer" title="Website" onClick={(e) => e.stopPropagation()}><Globe className="w-3.5 h-3.5" /></a>}
            {item.youtube_url && <a href={item.youtube_url} target="_blank" rel="noopener noreferrer" title="YouTube" style={{ color: "#FF0000" }} onClick={(e) => e.stopPropagation()}><Youtube className="w-3.5 h-3.5" /></a>}
            {item.instagram_url && <a href={item.instagram_url} target="_blank" rel="noopener noreferrer" title="Instagram" style={{ color: "#E4405F" }} onClick={(e) => e.stopPropagation()}><Instagram className="w-3.5 h-3.5" /></a>}
            {isMine && (
              <>
                <button onClick={onEdit} title="Edit" data-testid={`pulse-item-edit-${item.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={onDelete} title="Delete" className="text-red-600" data-testid={`pulse-item-del-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}
